import { useEffect, useMemo, useRef, useState } from 'react';

import { type IActions } from '../interface/IAction';
import { type IMergedSlot, PU_EVENT_LABELS, type PuEventType } from '../interface/pu-events';
import {
	DEFAULT_INEC_SCHEDULE,
	deriveNextExpected,
	deriveOverdue,
	getWindow,
	isNudgingActive,
	toCapturedAtMap,
	toCapturedSet,
} from '../lib/election-day-schedule';

const TICK_MS = 60_000;
/** Minimum gap between any two nudge toasts. */
const TOAST_MIN_GAP_MS = 15 * 60_000;

export interface INudgeEngine {
	/** The event the agent should capture next (lowest uncaptured). */
	nextExpected: PuEventType | null;
	/** Expected wall-clock time hint for the next event ("08:30") or null. */
	nextExpectedAt: string | null;
	/** Uncaptured events whose expected window has lapsed. */
	overdue: PuEventType[];
}

/**
 * Schedule-aware in-app nudges for the Election Day Pulse. Derives the
 * next-expected/overdue milestones every minute and fires the global toast
 * sparingly: at most once per event type, never more than one toast per
 * 15 minutes, suppressed while the Pulse panel is open (the agent is already
 * looking at the timeline) and outside an active election day.
 */
export const useNudgeEngine = (
	slots: IMergedSlot[],
	actions: IActions | undefined,
	options?: { suppressToasts?: boolean }
): INudgeEngine => {
	const [now, setNow] = useState(() => new Date());

	const actionsRef = useRef(actions);
	const suppressRef = useRef(!!options?.suppressToasts);
	useEffect(() => {
		actionsRef.current = actions;
		suppressRef.current = !!options?.suppressToasts;
	});

	// Anti-spam bookkeeping (refs — never state, no re-renders).
	const toastedRef = useRef<Set<PuEventType>>(new Set());
	const lastToastAtRef = useRef(0);

	useEffect(() => {
		const timer = setInterval(() => setNow(new Date()), TICK_MS);
		return () => clearInterval(timer);
	}, []);

	const derived = useMemo(() => {
		const captured = toCapturedSet(slots);
		const capturedAt = toCapturedAtMap(slots);
		const nextExpected = deriveNextExpected(captured);
		const overdue = deriveOverdue(captured, DEFAULT_INEC_SCHEDULE, now, capturedAt);
		const active = isNudgingActive(captured, DEFAULT_INEC_SCHEDULE, now);
		const window = nextExpected ? getWindow(DEFAULT_INEC_SCHEDULE, nextExpected) : undefined;
		return { nextExpected, nextExpectedAt: window?.expectedHHMM ?? null, overdue, active };
	}, [slots, now]);

	// Toast for the most pressing overdue event, within the anti-spam rules.
	useEffect(() => {
		if (!derived.active || suppressRef.current) return;
		const candidate = derived.overdue.find((eventType) => !toastedRef.current.has(eventType));
		if (!candidate) return;
		if (Date.now() - lastToastAtRef.current < TOAST_MIN_GAP_MS) return;
		toastedRef.current.add(candidate);
		lastToastAtRef.current = Date.now();
		actionsRef.current?.notificationGlobal?.(
			`Reminder: "${PU_EVENT_LABELS[candidate]}" has not been captured yet — tap it on your Election Day Pulse when it happens`,
			true
		);
	}, [derived]);

	return { nextExpected: derived.nextExpected, nextExpectedAt: derived.nextExpectedAt, overdue: derived.overdue };
};
